import { animate, state, style, transition, trigger } from '@angular/animations';
import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import { DfUiModule } from '@df/ui/df-ui.module';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { Subscription } from 'rxjs';
import { ProductComponent } from './product.component';

@Component({
    selector: 'product-quick-buy',
    templateUrl: 'product-quick-buy.html',
    styleUrls: ['product-quick-buy.scss'],
    standalone: true,
    imports: [DfUiModule, TranslationModule, NgClass],
    animations: [
        trigger('buttonHide', [
            state(
                'visible',
                style({
                    opacity: 1
                })
            ),
            state(
                'hidden',
                style({
                    opacity: 0,
                    transform: 'translate(100%)'
                })
            ),
            transition('* => *', animate('300ms 1000ms ease-out'))
        ])
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductQuickBuyComponent implements OnInit, OnDestroy {
    protected state: 'visible' | 'hidden' = 'visible';

    /**
     * If the animation should be played, or we just simply display quick add to basket button
     */
    @Input()
    showAnimation = false;

    private observer?: IntersectionObserver;
    private subscriptions = new Subscription();

    constructor(
        private elementRef: ElementRef,
        private changeDetectorRef: ChangeDetectorRef,
        private app: App,
        private productComponent: ProductComponent
    ) {}

    ngOnInit(): void {
        if (this.showAnimation && this.app.isPlatformBrowser) {
            this.observer = new IntersectionObserver(
                entries => {
                    if (entries[0].isIntersecting === true) {
                        this.setState('hidden');
                        this.cleanup();
                    }
                },
                {
                    rootMargin: '0px',
                    threshold: 1.0
                }
            );
            this.observer.observe(this.elementRef.nativeElement);
        }
        this.subscriptions.add(this.status.subscribe(() => this.changeDetectorRef.detectChanges()));
    }

    ngOnDestroy(): void {
        this.cleanup();
        this.subscriptions.unsubscribe();
    }

    get status(): Status {
        return this.productComponent._previewStatus;
    }

    private cleanup() {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = undefined;
        }
    }

    private setState(state: 'visible' | 'hidden') {
        if (this.state !== state) {
            this.state = state;
            this.changeDetectorRef.detectChanges();
        }
    }

    protected openPreview() {
        this.productComponent.togglePreview(true);
    }
}
